import { useCallback, useEffect, useState } from 'react';
import { buildStatusPayload } from '../adapters/statusWorkflowAdapter';
import { getStatusId } from '../adapters/statusFieldSyncAdapter';
import statusWorkflowService from '../services/statusWorkflowService';
import {
  filterStatusesForParent,
  mergeStatusRowPatch,
  patchStatusTriggers,
  patchSubstatusTriggers,
} from '../utils/statusTriggerUtils';
import { normalizeTriggerForSave } from '../utils/triggerFormUtils';

export default function useStatusTriggers({
  formId,
  workspaceId,
  parentKey = '',
  enabled = true,
} = {}) {
  const [statuses, setStatuses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadStatuses = useCallback(async () => {
    if (!enabled || !formId) {
      setStatuses([]);
      return;
    }

    setIsLoading(true);
    setLoadError('');

    try {
      const response = await statusWorkflowService.fetchStatuses({
        leadFormId: formId,
        typeOfStatus: parentKey || '',
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      setStatuses(parentKey ? filterStatusesForParent(rows, parentKey) : rows);
    } catch (error) {
      setLoadError(error?.message || 'Failed to load status triggers.');
      setStatuses([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, formId, parentKey]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const findFreshRow = useCallback(
    async (statusId) => {
      const response = await statusWorkflowService.fetchStatuses({
        leadFormId: formId,
        typeOfStatus: parentKey || '',
      });
      const rows = Array.isArray(response?.data) ? response.data : [];
      return rows.find((row) => String(getStatusId(row)) === String(statusId)) || null;
    },
    [formId, parentKey]
  );

  const saveStatusRow = useCallback(
    async (statusRow) => {
      if (!formId || !workspaceId || !statusRow) {
        return { success: false, error: 'Missing form context.' };
      }

      setIsSaving(true);
      try {
        const statusId = getStatusId(statusRow);
        const fresh = statusId ? await findFreshRow(statusId) : null;
        const merged = mergeStatusRowPatch(fresh, statusRow);
        const payload = buildStatusPayload(merged, {
          campaignId: workspaceId,
          leadFormId: formId,
          timestamp: Date.now(),
        });
        const response = await statusWorkflowService.saveStatus(payload);
        if (response?.status === 200) {
          await loadStatuses();
          return { success: true };
        }
        return { success: false, error: 'Failed to save triggers.' };
      } catch (error) {
        return { success: false, error: error?.message || 'Failed to save triggers.' };
      } finally {
        setIsSaving(false);
      }
    },
    [findFreshRow, formId, loadStatuses, workspaceId]
  );

  const updateStatusTriggers = useCallback(
    async (statusRow, triggers) => {
      const normalized = (triggers || []).map(normalizeTriggerForSave);
      const next = patchStatusTriggers(statusRow, normalized);
      return saveStatusRow(next);
    },
    [saveStatusRow]
  );

  const updateSubstatusTriggers = useCallback(
    async (statusRow, subName, subFieldKey, triggers) => {
      const normalized = (triggers || []).map(normalizeTriggerForSave);
      const next = patchSubstatusTriggers(statusRow, subName, subFieldKey, normalized);
      return saveStatusRow(next);
    },
    [saveStatusRow]
  );

  return {
    statuses,
    isLoading,
    loadError,
    isSaving,
    reload: loadStatuses,
    saveStatusRow,
    updateStatusTriggers,
    updateSubstatusTriggers,
  };
}
